// The write model: the entries as they really are, and the only code allowed to change them.
//
// Every change is a COMMAND. A command validates, writes, and announces what happened; nothing else
// in this feature calls `store.write`, exactly as nothing on the server writes past the command bus.
// The screen does not edit entries — it posts a command and redraws from what comes back. That is
// the whole of the discipline, and it is worth the two files it costs: the day an entry gains a
// field, or a change needs to be refused, there is one place where changes happen.
//
// COMMANDS ARE SERIALISED. Each one reads the document, changes it and writes it back, so two
// running at once would read the same state and the second would erase the first — a real race, not
// a theoretical one: deleting two entries quickly is a click and a click. They queue on one promise
// chain instead. There is no lock to take and none to forget.
//
// AN ENTRY'S IDENTITY IS ITS CONTENT, not the moment it was kept. Fingerprinting the answers rather
// than stamping a counter is what makes IMPORT IDEMPOTENT: importing the same file twice adds
// nothing the second time, and importing a file that overlaps with what is already here merges
// instead of doubling. Without that, moving a store between two browsers would be a one-way trip.
// What it gives up is a log — filling the same form with the same answers twice leaves one entry,
// with the later date. That is the right trade for a store whose question is "what have I kept",
// not "what have I done".

import { read, write, blank } from './store.js';

/** Bumped only if the shape of an entry changes in a way an older reader cannot survive. */
const FORMAT = 1;

const listeners = new Set();

/* The queue. Every command appends to it, so they run one after another whatever order they were
   called in. It never rejects: a failed command settles to its own outcome, and a chain that
   rejected once would refuse every command after it. */
let queue = Promise.resolve();

function serialise(work) {
	const next = queue.then(work, work);
	// Swallowed HERE and only here: the caller still sees the real outcome through `next`, but the
	// chain the NEXT command builds on must stay clean.
	queue = next.then(() => undefined, () => undefined);
	return next;
}

function announce(event) {
	listeners.forEach((listener) => {
		try {
			listener(event);
		} catch (ignored) {
			// A screen that throws while redrawing must not stop the store from working, nor stop the
			// other listeners from hearing what happened.
		}
	});
}

/** Called after every command, with what it did. The screen's only way of learning about changes. */
export function subscribe(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/*
 * A 64-bit fingerprint, as sixteen hex characters.
 *
 * Two FNV-1a passes with different offset bases rather than one: a single 32-bit pass is fine for a
 * few hundred entries and stops being obviously fine somewhere above that, and the second pass costs
 * one more multiply per character. Nothing here is protected by an id being hard to forge — it
 * identifies entries inside one person's own browser — so this is not a cryptographic hash and does
 * not pretend to be one. What it HAS to be is stable across browsers and versions, which rules out
 * anything derived from object key order or from `Math.random`.
 *
 * It hashes UTF-16 code units, not the bytes canonical FNV walks. That makes it ours rather than
 * the reference function, and it is deliberate: the same string gives the same answer in every
 * browser, which is the only property this needs.
 */
function fingerprint(text) {
	let left = 0x811c9dc5;
	let right = 0xc9dc5118;
	for (let index = 0; index < text.length; index += 1) {
		const code = text.charCodeAt(index);
		left = Math.imul(left ^ code, 0x01000193);
		right = Math.imul(right ^ code, 0x01000193);
	}
	return (left >>> 0).toString(16).padStart(8, '0') + (right >>> 0).toString(16).padStart(8, '0');
}

/* What an entry IS, for the purpose of being the same entry: where it came from and what was
   answered. Not the date, not the origin it was typed on, not the words of the labels — those are
   the reader's language, and the same answers kept in French and in English are the same answers. */
function identityOf(entry) {
	const answers = (entry.answers || []).map((answer) => answer.name + '=' + answer.value).join(' ');
	return fingerprint((entry.source || '') + ' ' + answers);
}

/* The canonical shape. Built here rather than trusted from the caller: everything that reaches this
   file arrives from a form or from another browser's export file, and only one of the two is ours.
   Unknown fields are dropped rather than carried, which is what stops an imported file from planting
   values nothing reads today and something reads tomorrow.

   ONE KIND LEFT. There were three — `form`, `tool`, `profile` — and the last two had a single writer
   between them, the « keep this in my browser » button on a tool's answer. With it gone, nothing can
   produce them, so `kind` is normalised to `form`: an old export file merges as what it is, a set of
   answers with an address to reopen them, and no branch survives to read a kind nothing writes. */
function canonical(draft) {
	const answers = (Array.isArray(draft.answers) ? draft.answers : [])
		.filter((answer) => answer && typeof answer.value === 'string' && answer.value.trim() !== '')
		.map((answer) => ({
			name: String(answer.name || ''),
			label: String(answer.label || answer.name || ''),
			value: String(answer.value).trim(),
		}));

	const entry = {
		format: FORMAT,
		kind: 'form',
		title: String(draft.title || '').trim(),
		errand: String(draft.errand || '').trim(),
		// The address this came from, kept whole so the entry can be REOPENED. It is what makes a
		// kept answer worth keeping: a list of values you cannot put back into the form that asked
		// for them is an archive, not a store.
		source: String(draft.source || '').trim(),
		// Which of our hosts it was typed on. Shown, not enforced — with the broker in place every
		// origin reads the same store, and this is how a person can still tell where an entry is from.
		origin: String(draft.origin || '').trim(),
		keptAt: typeof draft.keptAt === 'string' ? draft.keptAt : new Date().toISOString(),
		answers,
	};
	entry.id = identityOf(entry);
	return entry;
}

/** Everything kept, newest first. The one read the read model projects from. */
export function all() {
	return read().then((doc) => Object.values(doc.entries));
}

/**
 * KEEP — a filled form becomes an entry.
 *
 * Refuses an entry with no answers rather than storing an empty shell: a form opened and abandoned
 * is not something someone meant to keep, and a store full of blanks is a store people stop opening.
 */
export function keep(draft) {
	return serialise(() => {
		const entry = canonical(draft);
		if (!entry.answers.length) {
			return { ok: false, reason: 'empty' };
		}
		return read().then((doc) => {
			const known = doc.entries[entry.id];
			// Same content, kept again: the date moves, nothing else.
			doc.entries[entry.id] = known ? { ...known, keptAt: entry.keptAt } : entry;
			return write(doc).then((stored) => {
				const event = stored
					? { type: 'kept', id: entry.id, again: !!known }
					: { type: 'refused', reason: 'unwritable' };
				announce(event);
				return { ok: stored, id: entry.id, again: !!known };
			});
		});
	});
}

/**
 * FORGET — one entry goes.
 *
 * Hands back what it removed, which is what lets the screen offer an undo without holding a copy of
 * its own. A screen that kept its own shadow of an entry would be a second write model.
 */
export function forget(id) {
	return serialise(() => read().then((doc) => {
		const removed = doc.entries[id];
		if (!removed) {
			return { ok: false, reason: 'unknown' };
		}
		delete doc.entries[id];
		return write(doc).then((stored) => {
			announce(stored ? { type: 'forgotten', id, entry: removed }
				: { type: 'refused', reason: 'unwritable' });
			return { ok: stored, entry: removed };
		});
	}));
}

/** RESTORE — put back what FORGET handed over. The undo, and the import of a single entry. */
export function restore(entry) {
	return keep(entry);
}

/**
 * IMPORT — merge another browser's store into this one.
 *
 * MERGES, never replaces. An import that overwrote would make "I moved my laptop's data to my
 * phone" delete everything the phone had, and there is no copy anywhere to get it back from — we
 * never held one. Entries already here are left exactly as they are, including their date: the
 * local one is the one this person has been looking at.
 *
 * Refuses a file that is not one of ours before touching anything, so a wrong file changes nothing
 * rather than half-importing and stopping.
 */
export function importDocument(incoming) {
	return serialise(() => {
		if (!incoming || incoming.version !== 1 || !incoming.entries
			|| typeof incoming.entries !== 'object') {
			return { ok: false, reason: 'unreadable' };
		}
		const candidates = Object.values(incoming.entries)
			.filter((entry) => entry && typeof entry === 'object')
			.map(canonical)
			// The same refusal KEEP makes: no empty shells.
			.filter((entry) => entry.answers.length);

		return read().then((doc) => {
			let added = 0;
			let known = 0;
			candidates.forEach((entry) => {
				if (doc.entries[entry.id]) {
					known += 1;
					return;
				}
				doc.entries[entry.id] = entry;
				added += 1;
			});
			if (!added) {
				// Nothing to write, so nothing is written: a no-op import must not rewrite the store
				// and must not report a failure it did not have.
				announce({ type: 'imported', added: 0, known });
				return { ok: true, added: 0, known };
			}
			return write(doc).then((stored) => {
				announce(stored ? { type: 'imported', added, known }
					: { type: 'refused', reason: 'unwritable' });
				return { ok: stored, added, known };
			});
		});
	});
}

/** EXPORT — the document, exactly as it is stored. A copy, not a rendering of one. */
export function exportDocument() {
	return read();
}

/** CLEAR — everything goes. The one command with no undo, which is why the screen asks first. */
export function clear() {
	return serialise(() => write(blank()).then((stored) => {
		announce(stored ? { type: 'cleared' } : { type: 'refused', reason: 'unwritable' });
		return { ok: stored };
	}));
}

export { identityOf, canonical, FORMAT };
